import React from 'react';
import { format } from 'date-fns';
import { RESERVATION_VIEWS } from '../constants/editJobConstants';

const ReservationNavigator = ({ 
  reservationView, 
  setReservationView, 
  previousReservation, 
  nextReservation,
  job 
}) => {
  // Check service type for button visibility
  const timeBase = job?.serviceType?.timeBase;
  const isCheckoutBased = timeBase === 'CHECKOUT';
  const isCheckinBased = timeBase === 'CHECKIN';

  // Format date range for display
  const formatDateRange = (reservation) => {
    if (!reservation) return null;
    const checkIn = reservation.check_in ? format(new Date(reservation.check_in), "MMM d") : 'N/A';
    const checkOut = reservation.check_out ? format(new Date(reservation.check_out), "MMM d") : 'N/A';
    return (
      <span className="flex items-center justify-center gap-1">
        {checkIn}
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
        {checkOut}
      </span>
    );
  };

  // Determine job basis for title
  const getJobBasisTitle = () => {
    if (reservationView === RESERVATION_VIEWS.PREVIOUS) {
      return 'Previous Guest';
    } else if (reservationView === RESERVATION_VIEWS.NEXT) {
      return 'Next Reservation';
    } else {
      // Current reservation
      return 'Current reservation for Job';
    }
  };

  return (
    <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
      {/* Mobile Title Section */}
      <div className="text-center mb-4 sm:hidden">
        <h3 className="font-semibold text-gray-900 text-base">
          {getJobBasisTitle()}
        </h3>
        {reservationView === RESERVATION_VIEWS.CURRENT && (
          <div className="text-sm text-gray-700 mt-1">
            {formatDateRange(job?.reservation)}
          </div>
        )}
        <p className="text-xs text-gray-500 mt-1">
          {reservationView === RESERVATION_VIEWS.CURRENT && (
            job?.serviceType?.timeBase === 'CHECKOUT' ? '(Job timing based on guest checkout)' :
            job?.serviceType?.timeBase === 'CHECKIN' ? '(Job timing based on guest checkin)' :
            '(Current Job\'s Reservation)'
          )}
          {reservationView === RESERVATION_VIEWS.PREVIOUS && formatDateRange(previousReservation)}
          {reservationView === RESERVATION_VIEWS.NEXT && formatDateRange(nextReservation)}
        </p>
      </div>

      {/* Mobile Layout: Inline Buttons */}
      <div className={`flex space-x-3 sm:hidden ${
        (isCheckoutBased && reservationView === RESERVATION_VIEWS.CURRENT) ? 'justify-end' :
        (isCheckinBased && reservationView === RESERVATION_VIEWS.CURRENT) ? 'justify-start' : 
        ''
      }`}>
        {/* Previous Button - Hide for CHECKOUT-based jobs when on CURRENT view only */}
        {reservationView !== RESERVATION_VIEWS.PREVIOUS && !(isCheckoutBased && reservationView === RESERVATION_VIEWS.CURRENT) ? (
          <button 
            type="button"
            onClick={() => setReservationView(reservationView === RESERVATION_VIEWS.NEXT ? RESERVATION_VIEWS.CURRENT : RESERVATION_VIEWS.PREVIOUS)}
            disabled={reservationView === RESERVATION_VIEWS.CURRENT && !previousReservation}
            className={`flex flex-col items-center px-3 py-3 rounded-lg text-sm font-medium transition-all shadow-sm border ${
              (reservationView === RESERVATION_VIEWS.CURRENT && !previousReservation) 
                ? 'text-gray-400 cursor-not-allowed bg-gray-100 border-gray-200' 
                : 'text-blue-700 bg-white border-blue-200 hover:bg-blue-50 hover:border-blue-300 hover:shadow-md'
            }`}
          >
            <div className="flex items-center justify-start mb-1">
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-xs">{reservationView === RESERVATION_VIEWS.NEXT ? 'Job Reservation' : 'Previous'}</span>
            </div>
            {reservationView === RESERVATION_VIEWS.CURRENT && previousReservation && (
              <div className="flex items-center text-xs text-gray-600 gap-2">
                <span>{previousReservation.check_out ? format(new Date(previousReservation.check_out), "MMM d") : 'N/A'}</span>
                <div className="flex items-center bg-sky-600 text-white px-2 py-1 rounded-md shadow-sm">
                  <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </div>
            )}
          </button>
        ) : null}
        
        {/* Next Button - Hide for CHECKIN-based jobs when on CURRENT view only */}
        {reservationView !== RESERVATION_VIEWS.NEXT && !(isCheckinBased && reservationView === RESERVATION_VIEWS.CURRENT) ? (
          <button 
            type="button"
            onClick={() => setReservationView(reservationView === RESERVATION_VIEWS.PREVIOUS ? RESERVATION_VIEWS.CURRENT : RESERVATION_VIEWS.NEXT)}
            disabled={reservationView === RESERVATION_VIEWS.CURRENT && !nextReservation}
            className={`flex flex-col items-center px-3 py-3 rounded-lg text-sm font-medium transition-all shadow-sm border ${
              (reservationView === RESERVATION_VIEWS.CURRENT && !nextReservation)
                ? 'text-gray-400 cursor-not-allowed bg-gray-100 border-gray-200' 
                : 'text-blue-700 bg-white border-blue-200 hover:bg-blue-50 hover:border-blue-300 hover:shadow-md'
            }`}
          >
            <div className="flex items-center mb-1">
              <span className="text-xs">{reservationView === RESERVATION_VIEWS.PREVIOUS ? 'Job Reservation' : 'Next'}</span>
              <svg className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            {reservationView === RESERVATION_VIEWS.CURRENT && nextReservation && (
              <div className="flex items-center text-xs text-gray-600 gap-2">
                <div className="flex items-center bg-sky-600 text-white px-2 py-1 rounded-md shadow-sm">
                  <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <span>{nextReservation.check_in ? format(new Date(nextReservation.check_in), "MMM d") : 'N/A'}</span>
              </div>
            )}
            {reservationView === RESERVATION_VIEWS.PREVIOUS && (
              <span className="text-xs text-gray-600">
                Current Job
              </span>
            )}
          </button>
        ) : null}
      </div>

      {/* Desktop Layout: 3-Column Grid (2/7, 3/7, 2/7) */}
      <div className="hidden sm:grid grid-cols-7 gap-4 items-center">
        {/* Left Column - 2/7 width */}
        <div className="col-span-2 flex justify-center">
          {reservationView !== RESERVATION_VIEWS.PREVIOUS && !(isCheckoutBased && reservationView === RESERVATION_VIEWS.CURRENT) ? (
            <button 
              type="button"
              onClick={() => setReservationView(reservationView === RESERVATION_VIEWS.NEXT ? RESERVATION_VIEWS.CURRENT : RESERVATION_VIEWS.PREVIOUS)}
              disabled={reservationView === RESERVATION_VIEWS.CURRENT && !previousReservation}
              className={`flex flex-col items-center px-4 py-3 rounded-lg text-sm font-medium transition-all shadow-sm border ${
                (reservationView === RESERVATION_VIEWS.CURRENT && !previousReservation) 
                  ? 'text-gray-400 cursor-not-allowed bg-gray-100 border-gray-200' 
                  : 'text-blue-700 bg-white border-blue-200 hover:bg-blue-50 hover:border-blue-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-center mb-1">
                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {reservationView === RESERVATION_VIEWS.NEXT ? 'Job Reservation' : 'Previous Guest'}
              </div>
              {reservationView === RESERVATION_VIEWS.CURRENT && previousReservation && (
                <div className="flex items-center justify-center text-xs text-gray-600 gap-2">
                  <span>{previousReservation.check_out ? format(new Date(previousReservation.check_out), "MMM d") : 'N/A'}</span>
                  <div className="flex items-center bg-sky-600 text-white px-2 py-1 rounded-md shadow-sm">
                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              )}
            </button>
          ) : null}
        </div>
        
        {/* Center Column - 3/7 width - Always centered */}
        <div className="col-span-3 text-center">
          <h3 className="font-semibold text-gray-900 text-base">
            {getJobBasisTitle()}
          </h3>
          {reservationView === RESERVATION_VIEWS.CURRENT && (
            <div className="text-sm text-gray-700 mt-1">
              {formatDateRange(job?.reservation)}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">
            {reservationView === RESERVATION_VIEWS.CURRENT && (
              job?.serviceType?.timeBase === 'CHECKOUT' ? '(Job timing based on guest checkout)' :
              job?.serviceType?.timeBase === 'CHECKIN' ? '(Job timing based on guest checkin)' :
              '(Current Job\'s Reservation)'
            )}
            {reservationView === RESERVATION_VIEWS.PREVIOUS && formatDateRange(previousReservation)}
            {reservationView === RESERVATION_VIEWS.NEXT && formatDateRange(nextReservation)}
          </p>
        </div>
        
        {/* Right Column - 2/7 width */}
        <div className="col-span-2 flex justify-center">
          {reservationView !== RESERVATION_VIEWS.NEXT && !(isCheckinBased && reservationView === RESERVATION_VIEWS.CURRENT) ? (
            <button 
              type="button"
              onClick={() => setReservationView(reservationView === RESERVATION_VIEWS.PREVIOUS ? RESERVATION_VIEWS.CURRENT : RESERVATION_VIEWS.NEXT)}
              disabled={reservationView === RESERVATION_VIEWS.CURRENT && !nextReservation}
              className={`flex flex-col items-center px-4 py-3 rounded-lg text-sm font-medium transition-all shadow-sm border ${
                (reservationView === RESERVATION_VIEWS.CURRENT && !nextReservation)
                  ? 'text-gray-400 cursor-not-allowed bg-gray-100 border-gray-200' 
                  : 'text-blue-700 bg-white border-blue-200 hover:bg-blue-50 hover:border-blue-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-center mb-1">
                {reservationView === RESERVATION_VIEWS.PREVIOUS ? 'Job Reservation' : 'Next Guest'}
                <svg className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              {reservationView === RESERVATION_VIEWS.CURRENT && nextReservation && (
                <div className="flex items-center justify-center text-xs text-gray-600 gap-2">
                  <div className="flex items-center bg-sky-600 text-white px-2 py-1 rounded-md shadow-sm">
                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </div>
                  <span>{nextReservation.check_in ? format(new Date(nextReservation.check_in), "MMM d") : 'N/A'}</span>
                </div>
              )}
              {reservationView === RESERVATION_VIEWS.PREVIOUS && (
                <div className="text-xs text-gray-600">
                  Current Job
                </div>
              )}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ReservationNavigator;